/**
 * `/my-activity` — spec 01 §17's employee transparency surface.
 *
 * The shape file is `web/shapes/my-activity.md` and it was committed first. The order
 * of the regions is its §2, and the two orderings that were decided there rather than
 * here are worth restating:
 *
 *  - **The day comes before every number**, because "which day am I reading" has to
 *    be answered before any figure on the screen means anything.
 *  - **The score is below the fold.** Spec 01 §4.1.4 forbids gamifying surveillance;
 *    opening an employee's own screen on their score makes the score the subject of
 *    the screen, and the subject is their day.
 *
 * Every region fetches independently and renders its own skeleton, error and empty
 * state. A page-level spinner would hold four answers behind the slowest and a
 * page-level error would report one refusal as though the whole screen were broken —
 * and this is the screen spec 01 §17 requires to keep working.
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { addDays, localDateIn } from '../i18n/datetime';
import { useAuthenticatedUser } from '../auth/SessionProvider';
import { DateField } from '../components/DateField';
import { LastSync } from '../states/LastSync';
import { ScreenHeading } from '../shell/ScreenHeading';
import { AttendanceCard } from './myactivity/AttendanceCard';
import { CollectionRules } from './myactivity/CollectionRules';
import { CorrectionForm } from './myactivity/CorrectionForm';
import { HourTimeline } from './myactivity/HourTimeline';
import { ScorePanel } from './myactivity/ScorePanel';

export interface MyActivityScreenProps {
  /** Injected by tests so "today" and "which hours have happened" are values. */
  readonly nowMs?: number;
}

export function MyActivityScreen({ nowMs }: MyActivityScreenProps): ReactNode {
  const { t } = useI18n();
  const user = useAuthenticatedUser();
  const timezone = user.timezone;
  const now = nowMs ?? Date.now();

  // The company's local date, not UTC's and not the browser's. `toISOString().slice(0,
  // 10)` is the version of this that looks right and is a day out for a third of the
  // world for part of every day — and every key on this screen (attendance, the
  // rollup, the catalogue, a correction) is the company's local date.
  const [date, setDate] = useState(() => localDateIn(timezone, now));
  const [correcting, setCorrecting] = useState(false);
  const [filed, setFiled] = useState(0);
  const correctButtonRef = useRef<HTMLDivElement | null>(null);

  const closeForm = useCallback(() => {
    setCorrecting(false);
    // Focus returns to where it came from. Leaving it on a removed node drops a
    // keyboard user at the top of the document.
    correctButtonRef.current?.querySelector('button')?.focus();
  }, []);

  return (
    <div className="wp-stack">
      <div className="wp-screen-head">
        <ScreenHeading>{t('nav.myActivity')}</ScreenHeading>
        {/* Both timestamps, always. This screen reads no device heartbeat and no
            `wp_sync_state` row — there is no session `GET` for either — so both are
            null and render as "chưa từng" rather than being hidden. An absent
            last-updated line reads as an oversight; a stated absence does not. */}
        <LastSync agentSyncAt={null} achauSyncAt={null} timezone={timezone} />
      </div>

      {/* The day is the axis of this screen and it is the first thing under the
          title: every figure below is keyed on it, and a person on a phone has to be
          able to move a day without hunting. Three 44px targets, one row, and it
          wraps rather than shrinks at 390px. */}
      <div className="wp-daypicker wp-dayflip" data-testid="day-picker">
        <button
          type="button"
          className="wp-button wp-dayflip__step"
          onClick={() => {
            setDate((value) => addDays(value, -1));
          }}
          data-testid="day-prev"
        >
          {t('day.previous')}
        </button>
        <DateField
          className="wp-field wp-dayflip__field"
          id="day-input"
          testId="day-input"
          label={t('day.label')}
          value={date}
          onChange={setDate}
        />
        <button
          type="button"
          className="wp-button wp-dayflip__step"
          onClick={() => {
            setDate((value) => addDays(value, 1));
          }}
          data-testid="day-next"
        >
          {t('day.next')}
        </button>
      </div>

      <div ref={correctButtonRef}>
        <AttendanceCard
          date={date}
          timezone={timezone}
          reloadToken={filed}
          onCorrect={() => {
            setCorrecting(true);
          }}
        />
      </div>

      {correcting ? (
        <CorrectionForm
          date={date}
          timezone={timezone}
          onClose={closeForm}
          onFiled={() => {
            setFiled((value) => value + 1);
          }}
        />
      ) : null}

      <HourTimeline
        date={date}
        timezone={timezone}
        employeeId={user.employee_id}
        {...(nowMs === undefined ? {} : { nowMs })}
      />

      <CollectionRules date={date} />

      <ScorePanel date={date} employeeId={user.employee_id} />
    </div>
  );
}
